import moment from "moment";

// Simple toast function replacement
const toast = {
    error: (message) => {
        console.error('Toast Error:', message);
    },
    success: (message) => {
        console.log('Toast Success:', message);
    },
    info: (message) => {
        console.log('Toast Info:', message);
    }
};

export const utils = {
    addTimezoneOffset,
    arrayHasLength,
    capitalize,
    convertToDate,
    deleteStore,
    escapeRegexCharacters,
    filteredClasses,
    findFormErrors,
    generateDateToday,
    generateGoogleCalendarUrl,
    generateIcsCalendarFile,
    getAvatar,
    getContentName,
    getDayTime,
    getPasswordToken,
    deletePasswordToken,
    getRole,
    getPath,
    getStore,
    getStudentAvatar,
    handleResponse,
    handleAuthResponse,
    isClassExpired,
    isUserAdmin,
    isUserCreator,
    isUserManager,
    isUserStudent,
    isUserTeacher,
    objectHasLength,
    setStore,
    canUserEditClass,
    validateClassForm,
}

function setStore(name, content) {
    if (!name) return
    if (typeof content !== "string") {
        content = JSON.stringify(content)
    }
    return window.localStorage.setItem(name, content)
}

function getPasswordToken() {
    return (
        getStore("passwordToken") &&
        getStore("passwordToken") !== "undefined" &&
        getStore("passwordToken")
    )
}

function deletePasswordToken() {
    deleteStore("passwordToken")
    deleteStore("passwordToken_expires_at")
}
function getStore(name) {
    if (!name) return
    return window.localStorage.getItem(name)
}

function deleteStore(name) {
    if (!name) return
    return window.localStorage.removeItem(name)
}

function escapeRegexCharacters(str) {
    if (!str) return false
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

function canUserEditClass(cls, user) {
    // if class is null or still loading, return false
    if (!cls || cls.loading) {
        return false;
    }
    // if user is null or not loaded, return false
    if (!user) {
        return false;
    }
    
    // if user is admin, return true
    if (isUserAdmin(user)) {
        return true
    }
    // if the user is institution manager and the class is in the institution, return true
    if (isUserManager(user)) {
        let institutionIds = user.roles.filter(role => role.name === 'institution-manager')?.map(role => role.institution_id)
        return institutionIds.includes(cls.institution_id)
    }
    // if the user is the teacher of the class, return true
    return user.id === cls.teacher.id
}

function isUserManager(user) {
    return getRole(user) === "institution-manager"
}

function isUserTeacher(user) {
    return getRole(user) === "teacher"
}

function isUserStudent(user) {
    return getRole(user) === "student"
}

function isUserAdmin(user) {
    return getRole(user) === "super-admin" || getRole(user) === "admin"
}

function isUserCreator(user) {
    return getRole(user) === "creator"
}

function getRole(user) {
    return user?.role || 'student';
}

function handleResponse(response) {
    return response.text().then((text) => {
        if (response.ok) {
            const data = text && JSON.parse(text)
            return data
        } else {
            const data = text && JSON.parse(text)
            let error = {
                code: response.status,
                message: response.statusText
            }

            if (response.status === 401 && (data.message === "Unauthenticated." || data.message === "Unauthorized")) {
                deletePasswordToken()
                window.location.reload()
                return
            } else if (response.status === 403) {
                window.location.href = "/"
                return
            } else {
                if (response.status === 404) {
                    error = {
                        code: response.status,
                        message: response.statusText
                    }
                }

                if (response.status === 401) {
                    switch (data.message) {
                        case "Invalid login credentials":
                            error = {
                                code: 401,
                                message: "Incorrect email or password."
                            }
                            break
                        default:
                            error = {
                                code: 401,
                                message: data.message
                            }
                    }
                }
            }
            error.message && toast.error(error.message)
            return Promise.reject(error)
        }
    })
}

function handleAuthResponse(response) {
    return response.text().then((text) => {
        if (response.ok) {
            const data = text && JSON.parse(text)
            return data
        } else {
            const data = text && JSON.parse(text)
            let error = {
                code: response.status,
                message: response.statusText
            }

            if (response.status === 401 && (data.message === "Unauthenticated." || data.message === "Unauthorized")) {
                deletePasswordToken()
                window.location.reload()
                return
            } else {
                if (response.status === 404) {
                    error = {
                        code: response.status,
                        message: response.statusText
                    }
                }

                if (response.status === 401) {
                    deletePasswordToken()
                    window.location.reload()
                }
            }
            error.message && toast.error(error.message)
            return Promise.reject(error)
        }
    })
}

function getAvatar(user) {
    if(!user || !user.avatar) {
        return '/assets/img/favicon.png';
    }
    if (user?.avatar) {
        return user.avatar;
    }
    return '/assets/img/favicon.png';
}

function getStudentAvatar(user, isBig = false, banner = false) {
    if (!user) return ""

    if (user.avatar) {
        return user.avatar
    }

    function getFirstLetters() {
        if (user.name) {
            const names = user.name.split(" ")
            if (names.length > 1) {
                return names[0].charAt(0) + names[1].charAt(0)
            }
            return user.name.charAt(0)
        }
        return user.email.charAt(0)
    }

    const size = isBig ? "100" : "40"
    const letters = getFirstLetters().toUpperCase()
    const colors = [
        "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7",
        "#DDA0DD", "#98D8C8", "#F7DC6F", "#BB8FCE", "#85C1E9"
    ]
    const colorIndex = user.id ? user.id % colors.length : 0

    return `https://ui-avatars.com/api/?name=${letters}&background=${colors[colorIndex]}&color=fff&size=${size}`
}

function arrayHasLength(arr) {
    return Array.isArray(arr) && arr.length > 0
}

function objectHasLength(obj) {
    return obj && typeof obj === "object" && Object.keys(obj).length > 0
}

function findFormErrors(formValues) {
    const errors = {}
    return errors
}

function validateClassForm({
    key = undefined,
    value = undefined,
    formErrors,
    setFormErrors,
    formValues,
    creatorState = undefined
}) {
    return true
}

function filteredClasses(classes) {
    if (!classes) return []
    return classes.filter(cls => cls && cls.id)
}

function getPath(cls) {
    if (!cls) return ""

    if (cls.path) {
        return cls.path
    }

    if (cls.class_code) {
        return `/class/${cls.class_code}`
    }

    return ""
}

function isClassExpired(cls) {
    if (!cls || !cls.ends_at) return false
    return moment().isAfter(moment(cls.ends_at))
}

function getContentName(content_type_id) {
    const contentTypes = {
        1: "Video",
        2: "Document",
        3: "Quiz",
        4: "Assignment",
        5: "Link"
    }
    return contentTypes[content_type_id] || "Unknown"
}

function capitalize(word) {
    if (!word) return ""
    return word.charAt(0).toUpperCase() + word.slice(1)
}

function generateDateToday(date) {
    if (!date) return ""
    return moment(date).format("DD-MM-YYYY")
}

function convertToDate(date) {
    return moment(date).toDate()
}

function addTimezoneOffset(date) {
    return moment(date).local().format()
}

function getDayTime(date) {
    return moment(date).format("HH:mm")
}

function formatDateForCalendarUrl(date) {
    return moment(date).format("YYYYMMDDTHHmmss")
}

function generateGoogleCalendarUrl(session) {
    if (!session) return ""

    const startDate = formatDateForCalendarUrl(session.starts_at)
    const endDate = formatDateForCalendarUrl(session.ends_at)
    const title = encodeURIComponent(session.title || "Class Session")
    const description = encodeURIComponent(session.description || "")

    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${endDate}&details=${description}`
}

function generateIcsCalendarFile(session) {
    if (!session) return ""

    function convertHtmlToPlainText(html) {
        if (!html) return ""
        return html.replace(/<[^>]*>/g, "")
    }

    const startDate = moment(session.starts_at).format("YYYYMMDDTHHmmss")
    const endDate = moment(session.ends_at).format("YYYYMMDDTHHmmss")
    const title = session.title || "Class Session"
    const description = convertHtmlToPlainText(session.description || "")

    const icsContent = [
        "BEGIN:VCALENDAR",
        "VERSION:2.0",
        "BEGIN:VEVENT",
        `DTSTART:${startDate}`,
        `DTEND:${endDate}`,
        `SUMMARY:${title}`,
        `DESCRIPTION:${description}`,
        "END:VEVENT",
        "END:VCALENDAR"
    ].join("\r\n")

    return `data:text/calendar;charset=utf8,${encodeURIComponent(icsContent)}`
}
